import { useMemo } from 'react';
import { Tag, Typography } from 'antd';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { useStatusRender } from './useStatusRender';
import { usePrescriptionRender } from './usePrescriptionRender';
import { STATUS_FILTER_OPTIONS } from '../../constants';
import { useUserTimestampRender } from '@src/hooks';

const { Text } = Typography;

export const useMedicinesColumns = (): SmartDataTableProps['columns'] => {
  const getStatusConfig = useStatusRender();
  const getPrescriptionConfig = usePrescriptionRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  return useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'name',
        title: 'Medicine Name',
        dataIndex: 'name',
        sorter: true,
        minWidth: 200,
        ellipsis: true,
        render: (name: string, record: any) => (
          <div>
            <div style={{ fontWeight: 500, color: '#262626' }}>
              {name || 'N/A'}
            </div>
            {record.hsn && (
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                HSN: {record.hsn}
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'manufacturer',
        title: 'Manufacturer',
        dataIndex: 'manufacturer',
        sorter: true,
        minWidth: 150,
        ellipsis: true,
        render: (manufacturer: string, record: any) => (
          <div>
            <div style={{ fontWeight: 500, color: '#262626' }}>
              {manufacturer || 'N/A'}
            </div>
            {record.pack_size && (
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                {record.pack_size}
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'mrp',
        title: 'MRP',
        dataIndex: 'mrp',
        sorter: true,
        minWidth: 100,
        render: (mrp: number) => (
          <Text style={{ fontWeight: 500, color: '#52c41a' }}>
            ₹{mrp || '0.00'}
          </Text>
        ),
      },
      {
        key: 'gst_rate',
        title: 'GST Rate',
        dataIndex: 'gst_rate',
        sorter: true,
        minWidth: 100,
        render: (rate: number) => (
          <Tag color="blue" style={{ fontWeight: 500 }}>
            {rate ? `${rate}%` : '0%'}
          </Tag>
        ),
      },
      {
        key: 'requires_prescription',
        title: 'Prescription',
        dataIndex: 'requires_prescription',
        minWidth: 130,
        render: (requiresPrescription: number | boolean) => {
          const config = getPrescriptionConfig(requiresPrescription);
          return (
            <Tag
              icon={config.icon}
              style={{
                fontWeight: 500,
                backgroundColor: config.bgColor,
                color: config.textColor,
                borderColor: config.borderColor,
                border: `1px solid ${config.borderColor}`,
              }}
            >
              {config.text}
            </Tag>
          );
        },
      },
      {
        key: 'is_active',
        title: 'Status',
        dataIndex: 'is_active',
        minWidth: 100,
        filterable: true,
        filterType: 'multiselect',
        filterOptions: [...STATUS_FILTER_OPTIONS],
        render: (status: number | boolean) => {
          const config = getStatusConfig(status);
          return (
            <Tag
              icon={config.icon}
              style={{
                fontWeight: 500,
                backgroundColor: config.bgColor,
                color: config.textColor,
                borderColor: config.borderColor,
                border: `1px solid ${config.borderColor}`,
              }}
            >
              {config.text}
            </Tag>
          );
        },
      },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ],
    [getStatusConfig, getPrescriptionConfig, userTimestampRender]
  );
};
